/**
 * Sole owner of persisted authentication credentials. No other module reads
 * or writes these `localStorage` keys directly.
 *
 * Storage mechanism: `localStorage`, chosen deliberately (not by default) -
 * see `docs/frontend/AUTHENTICATION_GUIDE.md` "Token storage" section for
 * the full tradeoff analysis (localStorage vs. sessionStorage vs.
 * in-memory-only vs. httpOnly cookies). Summary: the backend issues both
 * tokens in a JSON response body, not a `Set-Cookie` header, so an
 * httpOnly-cookie design would require a backend change out of this
 * sprint's scope. `localStorage` is required for the refresh token to
 * survive a page reload without forcing re-login every time (the access
 * token's own 15-minute TTL already bounds its exposure window
 * regardless of where it's stored).
 */

export interface TokenPair {
  accessToken: string;
  refreshToken: string;
}

const ACCESS_TOKEN_KEY = "falcon.auth-token";
const REFRESH_TOKEN_KEY = "falcon.refresh-token";

export const tokenStorage = {
  getAccessToken(): string | null {
    return window.localStorage.getItem(ACCESS_TOKEN_KEY);
  },

  getRefreshToken(): string | null {
    return window.localStorage.getItem(REFRESH_TOKEN_KEY);
  },

  setTokens({ accessToken, refreshToken }: TokenPair): void {
    window.localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    window.localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  },

  clearTokens(): void {
    window.localStorage.removeItem(ACCESS_TOKEN_KEY);
    window.localStorage.removeItem(REFRESH_TOKEN_KEY);
  },
};
